const _jsxFileName = '';
function _optionalChain(ops) {
  let lastAccessLHS = undefined;
  let value = ops[0];
  let i = 1;
  while (i < ops.length) {
    const op = ops[i];
    const fn = ops[i + 1];
    i += 2;
    if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
      return undefined;
    }
    if (op === 'access' || op === 'optionalAccess') {
      lastAccessLHS = value;
      value = fn(value);
    } else if (op === 'call' || op === 'optionalCall') {
      value = fn((...args) => value.call(lastAccessLHS, ...args));
      lastAccessLHS = undefined;
    }
  }
  return value;
}
import React, { useState, useEffect, useRef } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
});
import {
  User,
  Mail,
  Phone,
  MapPin,
  Camera,
  Save,
  Shield,
  Lock,
  Bell,
  History,
  Smartphone,
  CheckCircle2,
  Building,
  BadgeCheck,
  ArrowLeft,
  X,
} from 'lucide-react';

const AdminProfile = ({ onNavigate, user, onUpdateUser, settings }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Profile Photo Upload State
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  // Form State — seeded from user prop, then overwritten by API fetch
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    department: 'Inventory & Vault',
    employeeId: '',
    location: '',
    bio: '',
    avatar: '',
  });

  const [security, setSecurity] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactor: true,
    emailNotifs: true,
    smsNotifs: false,
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Load real profile from API on mount
  useEffect(() => {
    if (!user?.id) return;
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/users/get-profile?user_id=${user.id}`, {
          headers: authHeaders(),
        });
        const data = await res.json();
        if (data.success && data.user) {
          const p = data.user;
          setProfile((prev) => ({
            ...prev,
            firstName: p.firstName || p.first_name || '',
            lastName: p.lastName || p.last_name || '',
            email: p.email || '',
            phone: p.phone || '',
            role: p.role || '',
            avatar: p.avatar || '',
            employeeId: `SG-${String(p.id).padStart(4, '0')}-${(p.role || 'STF').toUpperCase().slice(0, 3)}`,
          }));
        }
      } catch (e) {}
    };
    fetchProfile();
  }, [user?.id]);

  // Mock Activity Log
  const activities = [
    { action: 'Updated Gold Rates', date: 'Today, 10:23 AM', device: 'Desktop - Chrome' },
    {
      action: 'Added Product: Aurora Necklace',
      date: 'Yesterday, 4:45 PM',
      device: 'Desktop - Chrome',
    },
    { action: 'Exported Monthly Report', date: 'Oct 24, 2023', device: 'Tablet - Safari' },
    { action: 'Password Changed', date: 'Oct 15, 2023', device: 'Mobile - iOS' },
  ];

  const handlePhotoClick = () => setShowPhotoUploadModal(true);

  const handlePhotoSelect = (e) => {
    const file = _optionalChain([
      e,
      'access',
      (_) => _.target,
      'access',
      (_2) => _2.files,
      'optionalAccess',
      (_3) => _3[0],
    ]);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setSelectedPhoto(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoUpload = async () => {
    if (!selectedPhoto || !user?.id) return;
    setIsUploadingPhoto(true);
    try {
      const res = await fetch(`${API_BASE_URL}/users/update-avatar`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ user_id: user.id, avatar: selectedPhoto }),
      });
      const data = await res.json();
      if (data.success) {
        setProfile((prev) => ({ ...prev, avatar: selectedPhoto }));
        if (onUpdateUser) onUpdateUser({ ...user, avatar: selectedPhoto });
        setSelectedPhoto(null);
        setShowPhotoUploadModal(false);
        showToast('Profile photo updated successfully!');
      } else {
        showToast(data.error || 'Failed to upload photo', 'error');
      }
    } catch (e) {
      showToast('Error uploading photo. Please try again.', 'error');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const triggerFileInput = () => {
    _optionalChain([
      fileInputRef,
      'access',
      (_4) => _4.current,
      'optionalAccess',
      (_5) => _5.click,
      'call',
      (_6) => _6(),
    ]);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!user?.id) return;
    setIsSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/users/update-profile`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          user_id: user.id,
          firstName: profile.firstName,
          lastName: profile.lastName,
          email: profile.email,
          phone: profile.phone,
        }),
      });
      const data = await res.json();
      if (data.success) {
        if (onUpdateUser)
          onUpdateUser({
            ...user,
            name: `${profile.firstName} ${profile.lastName}`.trim(),
            email: profile.email,
            phone: profile.phone,
          });
        showToast('Profile updated successfully.');
      } else {
        showToast(data.error || 'Failed to update profile', 'error');
      }
    } catch (e) {
      showToast('Error saving profile. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!security.currentPassword || !security.newPassword || !security.confirmPassword) {
      showToast('Please fill in all password fields', 'error');
      return;
    }
    if (security.newPassword !== security.confirmPassword) {
      showToast('New passwords do not match', 'error');
      return;
    }
    if (security.newPassword.length < 6) {
      showToast('New password must be at least 6 characters', 'error');
      return;
    }
    setIsChangingPassword(true);
    try {
      const res = await fetch(`${API_BASE_URL}/users/change-password`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          current_password: security.currentPassword,
          new_password: security.newPassword,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSecurity({ ...security, currentPassword: '', newPassword: '', confirmPassword: '' });
        showToast('Password updated successfully.');
      } else {
        showToast(data.error || 'Failed to update password', 'error');
      }
    } catch (e) {
      showToast('Error updating password. Please try again.', 'error');
    } finally {
      setIsChangingPassword(false);
    }
  };

  return React.createElement(
    'div',
    {
      className: 'pt-44 pb-12 min-h-screen bg-stone-50',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 96 },
    },
    toast &&
      React.createElement(
        'div',
        {
          className: `fixed top-6 right-6 z-[999] flex items-center gap-3 px-5 py-4 rounded-sm shadow-xl text-sm font-medium transition-all ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-stone-900 text-white'}`,
          style: { minWidth: '280px' },
        },
        React.createElement('span', null, toast.message),
        React.createElement(
          'button',
          {
            onClick: () => setToast(null),
            className: 'ml-auto text-white/70 hover:text-white text-lg leading-none',
          },
          '×'
        )
      ),
    React.createElement(
      'div',
      {
        className: 'max-w-6xl mx-auto px-6',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 97 },
      },

      /* Profile Photo Upload Modal */
      showPhotoUploadModal &&
        React.createElement(
          'div',
          {
            className: 'fixed inset-0 z-[200] flex items-center justify-center',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 101 },
          },
          React.createElement('div', {
            className: 'absolute inset-0 bg-stone-900/70 backdrop-blur-sm',
            onClick: () => setShowPhotoUploadModal(false),
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 102 },
          }),
          React.createElement(
            'div',
            {
              className:
                'relative bg-white w-full max-w-md rounded-sm shadow-2xl animate-fade-up-enter fade-up-active mx-4 overflow-hidden',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 103 },
            },
            React.createElement(
              'div',
              {
                className: 'bg-stone-900 text-white p-6 flex justify-between items-center',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 104 },
              },
              React.createElement(
                'div',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 105 } },
                React.createElement(
                  'h3',
                  {
                    className: 'font-serif text-xl',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 106 },
                  },
                  'Update Profile Photo'
                ),
                React.createElement(
                  'p',
                  {
                    className: 'text-xs text-stone-400',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 107 },
                  },
                  'Choose a new profile picture'
                )
              ),
              React.createElement(
                'button',
                {
                  onClick: () => setShowPhotoUploadModal(false),
                  className: 'text-stone-400 hover:text-white',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 109 },
                },
                React.createElement(X, {
                  size: 20,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 110 },
                })
              )
            ),

            React.createElement(
              'div',
              {
                className: 'p-8 space-y-6',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 114 },
              },
              /* Hidden file input */
              React.createElement('input', {
                type: 'file',
                ref: fileInputRef,
                onChange: handlePhotoSelect,
                accept: 'image/*',
                className: 'hidden',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 116 },
              }),

              /* Preview Area */
              React.createElement(
                'div',
                {
                  className: 'flex flex-col items-center',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 125 },
                },
                React.createElement(
                  'div',
                  {
                    className:
                      'w-40 h-40 rounded-full border-4 border-gold-200 overflow-hidden shadow-xl mb-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 126 },
                  },
                  React.createElement('img', {
                    src: selectedPhoto || profile.avatar,
                    alt: 'Profile Preview',
                    className: 'w-full h-full object-cover',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 127 },
                  })
                ),

                selectedPhoto
                  ? React.createElement(
                      'p',
                      {
                        className: 'text-xs text-green-600 font-bold',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 135 },
                      },
                      'New photo selected'
                    )
                  : React.createElement(
                      'p',
                      {
                        className: 'text-xs text-stone-400',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 137 },
                      },
                      'Current profile photo'
                    )
              ),

              /* Action Buttons */
              React.createElement(
                'div',
                {
                  className: 'flex flex-col gap-3',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 142 },
                },
                React.createElement(
                  'button',
                  {
                    onClick: triggerFileInput,
                    className:
                      'w-full border-2 border-dashed border-stone-300 py-4 px-4 rounded-sm hover:border-gold-500 hover:bg-stone-50 transition-colors flex items-center justify-center gap-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 143 },
                  },

                  React.createElement(Camera, {
                    size: 18,
                    className: 'text-stone-400',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 147 },
                  }),
                  React.createElement(
                    'span',
                    {
                      className: 'text-sm font-medium',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 148 },
                    },
                    'Choose New Photo'
                  )
                ),

                React.createElement(
                  'div',
                  {
                    className: 'flex gap-3',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 151 },
                  },
                  React.createElement(
                    'button',
                    {
                      onClick: () => setShowPhotoUploadModal(false),
                      className:
                        'flex-1 border border-stone-300 text-stone-600 py-3 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 152 },
                    },
                    'Cancel'
                  ),
                  React.createElement(
                    'button',
                    {
                      onClick: handlePhotoUpload,
                      disabled: !selectedPhoto || isUploadingPhoto,
                      className:
                        'flex-1 bg-stone-900 text-white py-3 text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 158 },
                    },

                    isUploadingPhoto
                      ? React.createElement(
                          React.Fragment,
                          null,
                          React.createElement('div', {
                            className:
                              'w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 165 },
                          }),
                          'Uploading...'
                        )
                      : 'Upload Photo'
                  )
                )
              )
            )
          )
        ),

      /* Header */
      React.createElement(
        'div',
        {
          className:
            'flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 180 },
        },
        React.createElement(
          'div',
          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 181 } },
          (user?.role === 'admin' || (user?.permissions || []).includes('view_dashboard')) &&
            React.createElement(
              'button',
              {
                onClick: () => onNavigate('admin-dashboard'),
                className:
                  'flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-2 transition-colors',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 182 },
              },
              React.createElement(ArrowLeft, {
                size: 14,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 183 },
              }),
              ' Back to Dashboard'
            ),
          React.createElement(
            'h1',
            {
              className: 'font-serif text-3xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 185 },
            },
            'Admin Profile'
          ),
          React.createElement(
            'p',
            {
              className: 'text-stone-500 font-light mt-2 text-sm',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 186 },
            },
            'Manage your credentials, preferences, and personal information.'
          )
        ),

        React.createElement(
          'button',
          {
            onClick: handleSave,
            disabled: isSaving,
            className:
              'px-8 py-3 bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors flex items-center gap-2 shadow-lg disabled:opacity-70',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 189 },
          },

          isSaving ? 'Saving...' : 'Save Changes',
          React.createElement(Save, {
            size: 16,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 195 },
          })
        )
      ),

      React.createElement(
        'div',
        {
          className: 'grid grid-cols-1 lg:grid-cols-3 gap-8',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 199 },
        },

        /* Left Column: Profile Card */
        React.createElement(
          'div',
          {
            className: 'lg:col-span-1 space-y-8',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 202 },
          },

          /* ID Card */
          React.createElement(
            'div',
            {
              className:
                'bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden relative',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 205 },
            },
            React.createElement(
              'div',
              {
                className: 'h-32 bg-stone-900 relative overflow-hidden',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 206 },
              },
              React.createElement('div', {
                className:
                  "absolute inset-0 opacity-30 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]",
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 207 },
              }),
              React.createElement('div', {
                className: 'absolute inset-0 bg-gradient-to-r from-gold-600/20 to-transparent',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 208 },
              })
            ),

            React.createElement(
              'div',
              {
                className: 'px-6 pb-6 text-center -mt-16 relative',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 211 },
              },
              React.createElement(
                'div',
                {
                  className: 'relative inline-block group cursor-pointer',
                  onClick: handlePhotoClick,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 212 },
                },

                React.createElement(
                  'div',
                  {
                    className:
                      'w-32 h-32 rounded-full border-4 border-white bg-stone-200 overflow-hidden shadow-lg mx-auto',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 216 },
                  },
                  React.createElement('img', {
                    src: profile.avatar,
                    alt: 'Admin Profile',
                    className:
                      'w-full h-full object-cover transition-transform duration-500 group-hover:scale-110',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 217 },
                  })
                ),
                React.createElement(
                  'div',
                  {
                    className:
                      'absolute bottom-1 right-1 bg-stone-900 text-white p-2 rounded-full hover:bg-gold-600 transition-colors shadow-md',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 223 },
                  },
                  React.createElement(Camera, {
                    size: 16,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 224 },
                  })
                )
              ),

              React.createElement(
                'h2',
                {
                  className: 'font-serif text-2xl text-stone-900 mt-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 228 },
                },
                profile.firstName,
                ' ',
                profile.lastName
              ),
              React.createElement(
                'p',
                {
                  className: 'text-gold-600 text-xs uppercase tracking-widest font-bold mb-1',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 229 },
                },
                profile.role
              ),
              React.createElement(
                'p',
                {
                  className: 'text-stone-400 text-xs flex items-center justify-center gap-1',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 230 },
                },
                React.createElement(Building, {
                  size: 12,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 231 },
                }),
                ' ',
                profile.department
              ),

              React.createElement(
                'div',
                {
                  className: 'mt-6 flex justify-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 234 },
                },
                React.createElement(
                  'span',
                  {
                    className:
                      'bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-[10px] uppercase font-bold border border-emerald-100 flex items-center gap-1',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 235 },
                  },
                  React.createElement(CheckCircle2, {
                    size: 12,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 236 },
                  }),
                  ' Active Status'
                ),
                React.createElement(
                  'span',
                  {
                    className:
                      'bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-[10px] uppercase font-bold border border-blue-100 flex items-center gap-1',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 238 },
                  },
                  React.createElement(BadgeCheck, {
                    size: 12,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 239 },
                  }),
                  ' Verified'
                )
              )
            ),

            React.createElement(
              'div',
              {
                className: 'border-t border-stone-100 p-6 bg-stone-50/50',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 244 },
              },
              React.createElement(
                'div',
                {
                  className: 'flex justify-between items-center text-sm mb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 245 },
                },
                React.createElement(
                  'span',
                  {
                    className: 'text-stone-500',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 246 },
                  },
                  'Employee ID'
                ),
                React.createElement(
                  'span',
                  {
                    className: 'font-mono text-stone-900',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 247 },
                  },
                  profile.employeeId
                )
              ),
              React.createElement(
                'div',
                {
                  className: 'flex justify-between items-center text-sm',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 249 },
                },
                React.createElement(
                  'span',
                  {
                    className: 'text-stone-500',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 250 },
                  },
                  'Maison Location'
                ),
                React.createElement(
                  'span',
                  {
                    className: 'text-stone-900',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 251 },
                  },
                  profile.location
                )
              )
            )
          ),

          /* Company Contact Info */
          React.createElement(
            'div',
            { className: 'bg-white border border-stone-200 shadow-sm rounded-sm p-6' },
            React.createElement(
              'h3',
              { className: 'text-xs uppercase tracking-widest text-stone-400 font-bold mb-4' },
              'Company Information'
            ),
            React.createElement(
              'div',
              { className: 'space-y-4' },
              React.createElement(
                'div',
                { className: 'flex items-center gap-3' },
                React.createElement(
                  'div',
                  {
                    className:
                      'w-8 h-8 rounded-full bg-stone-50 flex items-center justify-center text-gold-600',
                  },
                  React.createElement(Mail, { size: 16 })
                ),
                React.createElement(
                  'div',
                  null,
                  React.createElement(
                    'p',
                    { className: 'text-[10px] uppercase text-stone-400' },
                    'Business Email'
                  ),
                  React.createElement(
                    'p',
                    { className: 'text-sm text-stone-900' },
                    (settings && settings.contactEmail) || '—'
                  )
                )
              ),
              React.createElement(
                'div',
                { className: 'flex items-center gap-3' },
                React.createElement(
                  'div',
                  {
                    className:
                      'w-8 h-8 rounded-full bg-stone-50 flex items-center justify-center text-gold-600',
                  },
                  React.createElement(Phone, { size: 16 })
                ),
                React.createElement(
                  'div',
                  null,
                  React.createElement(
                    'p',
                    { className: 'text-[10px] uppercase text-stone-400' },
                    'Business Phone'
                  ),
                  React.createElement(
                    'p',
                    { className: 'text-sm text-stone-900' },
                    (settings && settings.contactPhone) || '—'
                  )
                )
              ),
              React.createElement(
                'div',
                { className: 'flex items-start gap-3' },
                React.createElement(
                  'div',
                  {
                    className:
                      'w-8 h-8 rounded-full bg-stone-50 flex items-center justify-center text-gold-600 flex-shrink-0 mt-0.5',
                  },
                  React.createElement(MapPin, { size: 16 })
                ),
                React.createElement(
                  'div',
                  { className: 'flex-1 min-w-0' },
                  React.createElement(
                    'p',
                    { className: 'text-[10px] uppercase text-stone-400' },
                    'Address'
                  ),
                  React.createElement(
                    'p',
                    { className: 'text-sm text-stone-900 leading-snug' },
                    (settings && settings.address) || '—'
                  )
                )
              )
            )
          )
        ),

        /* Right Column: Settings Forms */
        React.createElement(
          'div',
          {
            className: 'lg:col-span-2 space-y-8',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 293 },
          },

          /* Personal Details Form */
          React.createElement(
            'div',
            {
              className: 'bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 296 },
            },
            React.createElement(
              'div',
              {
                className:
                  'px-6 py-4 border-b border-stone-100 bg-stone-50/30 flex justify-between items-center',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 297 },
              },
              React.createElement(
                'h3',
                {
                  className: 'font-serif text-lg text-stone-900',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 298 },
                },
                'Personal Details'
              ),
              React.createElement(User, {
                size: 16,
                className: 'text-stone-400',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 299 },
              })
            ),
            React.createElement(
              'div',
              {
                className: 'p-8 grid grid-cols-1 md:grid-cols-2 gap-6',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 301 },
              },
              React.createElement(
                'div',
                {
                  className: 'space-y-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 302 },
                },
                React.createElement(
                  'label',
                  {
                    className: 'text-xs uppercase tracking-widest text-stone-500 font-bold',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 303 },
                  },
                  'First Name'
                ),
                React.createElement('input', {
                  type: 'text',
                  value: profile.firstName,
                  onChange: (e) => setProfile({ ...profile, firstName: e.target.value }),
                  className:
                    'w-full border border-stone-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:ring-1 focus:ring-gold-500 focus:outline-none rounded-sm bg-white',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 304 },
                })
              ),
              React.createElement(
                'div',
                {
                  className: 'space-y-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 311 },
                },
                React.createElement(
                  'label',
                  {
                    className: 'text-xs uppercase tracking-widest text-stone-500 font-bold',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 312 },
                  },
                  'Last Name'
                ),
                React.createElement('input', {
                  type: 'text',
                  value: profile.lastName,
                  onChange: (e) => setProfile({ ...profile, lastName: e.target.value }),
                  className:
                    'w-full border border-stone-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:ring-1 focus:ring-gold-500 focus:outline-none rounded-sm bg-white',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 313 },
                })
              ),

              /* ADDED CONTACT INPUTS */
              React.createElement(
                'div',
                {
                  className: 'space-y-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 322 },
                },
                React.createElement(
                  'label',
                  {
                    className: 'text-xs uppercase tracking-widest text-stone-500 font-bold',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 323 },
                  },
                  'Email Address'
                ),
                React.createElement('input', {
                  type: 'email',
                  value: profile.email,
                  onChange: (e) => setProfile({ ...profile, email: e.target.value }),
                  className:
                    'w-full border border-stone-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:ring-1 focus:ring-gold-500 focus:outline-none rounded-sm bg-white',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 324 },
                })
              ),
              React.createElement(
                'div',
                {
                  className: 'space-y-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 331 },
                },
                React.createElement(
                  'label',
                  {
                    className: 'text-xs uppercase tracking-widest text-stone-500 font-bold',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 332 },
                  },
                  'Phone Number'
                ),
                React.createElement('input', {
                  type: 'text',
                  value: profile.phone,
                  onChange: (e) => setProfile({ ...profile, phone: e.target.value }),
                  className:
                    'w-full border border-stone-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:ring-1 focus:ring-gold-500 focus:outline-none rounded-sm bg-white',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 333 },
                })
              ),

              React.createElement(
                'div',
                {
                  className: 'space-y-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 341 },
                },
                React.createElement(
                  'label',
                  {
                    className: 'text-xs uppercase tracking-widest text-stone-500 font-bold',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 342 },
                  },
                  'Official Title'
                ),
                React.createElement('input', {
                  type: 'text',
                  value: profile.role,
                  onChange: (e) => setProfile({ ...profile, role: e.target.value }),
                  className:
                    'w-full border border-stone-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:ring-1 focus:ring-gold-500 focus:outline-none rounded-sm bg-white',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 343 },
                })
              ),
              React.createElement(
                'div',
                {
                  className: 'space-y-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 350 },
                },
                React.createElement(
                  'label',
                  {
                    className: 'text-xs uppercase tracking-widest text-stone-500 font-bold',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 351 },
                  },
                  'Department'
                ),
                React.createElement(
                  'select',
                  {
                    value: profile.department,
                    onChange: (e) => setProfile({ ...profile, department: e.target.value }),
                    className:
                      'w-full border border-stone-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:ring-1 focus:ring-gold-500 focus:outline-none rounded-sm bg-white',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 352 },
                  },

                  React.createElement(
                    'option',
                    { __self: this, __source: { fileName: _jsxFileName, lineNumber: 357 } },
                    'Inventory & Vault'
                  ),
                  React.createElement(
                    'option',
                    { __self: this, __source: { fileName: _jsxFileName, lineNumber: 358 } },
                    'Sales & Concierge'
                  ),
                  React.createElement(
                    'option',
                    { __self: this, __source: { fileName: _jsxFileName, lineNumber: 359 } },
                    'Executive Management'
                  ),
                  React.createElement(
                    'option',
                    { __self: this, __source: { fileName: _jsxFileName, lineNumber: 360 } },
                    'Logistics'
                  )
                )
              ),

              React.createElement(
                'div',
                {
                  className: 'space-y-2 md:col-span-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 364 },
                },
                React.createElement(
                  'label',
                  {
                    className: 'text-xs uppercase tracking-widest text-stone-500 font-bold',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 365 },
                  },
                  'Location / Office'
                ),
                React.createElement('input', {
                  type: 'text',
                  value: profile.location,
                  onChange: (e) => setProfile({ ...profile, location: e.target.value }),
                  className:
                    'w-full border border-stone-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:ring-1 focus:ring-gold-500 focus:outline-none rounded-sm bg-white',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 366 },
                })
              ),

              React.createElement(
                'div',
                {
                  className: 'md:col-span-2 space-y-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 374 },
                },
                React.createElement(
                  'label',
                  {
                    className: 'text-xs uppercase tracking-widest text-stone-500 font-bold',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 375 },
                  },
                  'Professional Bio'
                ),
                React.createElement('textarea', {
                  rows: 3,
                  value: profile.bio,
                  onChange: (e) => setProfile({ ...profile, bio: e.target.value }),
                  className:
                    'w-full border border-stone-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:ring-1 focus:ring-gold-500 focus:outline-none rounded-sm bg-white resize-none',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 376 },
                }),
                React.createElement(
                  'p',
                  {
                    className: 'text-[10px] text-stone-400 text-right',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 382 },
                  },
                  'Visible to internal staff only.'
                )
              )
            )
          ),

          /* Security Section */
          React.createElement(
            'div',
            {
              className: 'bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 388 },
            },
            React.createElement(
              'div',
              {
                className:
                  'px-6 py-4 border-b border-stone-100 bg-stone-50/30 flex justify-between items-center',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 389 },
              },
              React.createElement(
                'h3',
                {
                  className: 'font-serif text-lg text-stone-900',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 390 },
                },
                'Security & Access'
              ),
              React.createElement(Shield, {
                size: 16,
                className: 'text-stone-400',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 391 },
              })
            ),
            React.createElement(
              'div',
              {
                className: 'p-8',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 393 },
              },
              React.createElement(
                'div',
                {
                  className: 'grid grid-cols-1 md:grid-cols-2 gap-8 mb-8',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 394 },
                },
                React.createElement(
                  'div',
                  {
                    className: 'space-y-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 395 },
                  },
                  React.createElement(
                    'h4',
                    {
                      className: 'text-sm font-bold text-stone-900 flex items-center gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 396 },
                    },
                    React.createElement(Lock, {
                      size: 14,
                      className: 'text-gold-600',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 397 },
                    }),
                    ' Change Password'
                  ),
                  React.createElement('input', {
                    type: 'password',
                    placeholder: 'Current Password',
                    value: security.currentPassword,
                    onChange: (e) => setSecurity({ ...security, currentPassword: e.target.value }),
                    className:
                      'w-full border border-stone-300 px-4 py-2 text-sm focus:border-gold-500 focus:outline-none rounded-sm',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 399 },
                  }),
                  React.createElement('input', {
                    type: 'password',
                    placeholder: 'New Password',
                    value: security.newPassword,
                    onChange: (e) => setSecurity({ ...security, newPassword: e.target.value }),
                    className:
                      'w-full border border-stone-300 px-4 py-2 text-sm focus:border-gold-500 focus:outline-none rounded-sm',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 404 },
                  }),
                  React.createElement('input', {
                    type: 'password',
                    placeholder: 'Confirm New Password',
                    value: security.confirmPassword,
                    onChange: (e) => setSecurity({ ...security, confirmPassword: e.target.value }),
                    className:
                      'w-full border border-stone-300 px-4 py-2 text-sm focus:border-gold-500 focus:outline-none rounded-sm',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 409 },
                  }),
                  React.createElement(
                    'button',
                    {
                      type: 'button',
                      onClick: handleChangePassword,
                      disabled: isChangingPassword,
                      className:
                        'w-full bg-stone-900 text-white py-2 text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors disabled:opacity-50',
                    },
                    isChangingPassword ? 'Updating...' : 'Update Password'
                  )
                ),

                React.createElement(
                  'div',
                  {
                    className: 'space-y-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 416 },
                  },
                  React.createElement(
                    'h4',
                    {
                      className: 'text-sm font-bold text-stone-900 flex items-center gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 417 },
                    },
                    React.createElement(Smartphone, {
                      size: 14,
                      className: 'text-gold-600',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 418 },
                    }),
                    ' Two-Factor Authentication'
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'bg-stone-50 p-4 rounded-sm border border-stone-100',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 420 },
                    },
                    React.createElement(
                      'div',
                      {
                        className: 'flex items-center justify-between mb-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 421 },
                      },
                      React.createElement(
                        'span',
                        {
                          className: 'text-sm font-medium text-stone-800',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 422 },
                        },
                        'Authenticator App'
                      ),
                      React.createElement(
                        'label',
                        {
                          className: 'relative inline-flex items-center cursor-pointer',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 423 },
                        },
                        React.createElement('input', {
                          type: 'checkbox',
                          checked: security.twoFactor,
                          onChange: (e) =>
                            setSecurity({ ...security, twoFactor: e.target.checked }),
                          className: 'sr-only peer',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 424 },
                        }),
                        React.createElement('div', {
                          className:
                            "w-9 h-5 bg-stone-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-gold-600",
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 425 },
                        })
                      )
                    ),
                    React.createElement(
                      'p',
                      {
                        className: 'text-xs text-stone-500 leading-relaxed',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 428 },
                      },
                      'Secure your account by requiring a code from your mobile device when logging in.'
                    )
                  ),

                  React.createElement(
                    'h4',
                    {
                      className: 'text-sm font-bold text-stone-900 flex items-center gap-2 mt-6',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 433 },
                    },
                    React.createElement(Bell, {
                      size: 14,
                      className: 'text-gold-600',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 434 },
                    }),
                    ' Notifications'
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'space-y-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 436 },
                    },
                    React.createElement(
                      'label',
                      {
                        className: 'flex items-center gap-2 text-sm text-stone-600 cursor-pointer',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 437 },
                      },
                      React.createElement('input', {
                        type: 'checkbox',
                        checked: security.emailNotifs,
                        onChange: (e) =>
                          setSecurity({ ...security, emailNotifs: e.target.checked }),
                        className: 'accent-gold-600',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 438 },
                      }),
                      'Email Alerts for Login'
                    ),
                    React.createElement(
                      'label',
                      {
                        className: 'flex items-center gap-2 text-sm text-stone-600 cursor-pointer',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 441 },
                      },
                      React.createElement('input', {
                        type: 'checkbox',
                        checked: security.smsNotifs,
                        onChange: (e) => setSecurity({ ...security, smsNotifs: e.target.checked }),
                        className: 'accent-gold-600',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 442 },
                      }),
                      'SMS Alerts for High Value Orders'
                    )
                  )
                )
              ),

              React.createElement(
                'div',
                {
                  className: 'border-t border-stone-100 pt-6',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 449 },
                },
                React.createElement(
                  'h4',
                  {
                    className:
                      'text-xs uppercase tracking-widest text-stone-500 font-bold mb-4 flex items-center gap-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 450 },
                  },
                  React.createElement(History, {
                    size: 14,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 451 },
                  }),
                  ' Recent Session Activity'
                ),
                React.createElement(
                  'div',
                  {
                    className: 'overflow-x-auto',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 453 },
                  },
                  React.createElement(
                    'table',
                    {
                      className: 'w-full text-left',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 454 },
                    },
                    React.createElement(
                      'thead',
                      {
                        className: 'bg-stone-100 text-[10px] uppercase text-stone-500',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 455 },
                      },
                      React.createElement(
                        'tr',
                        { __self: this, __source: { fileName: _jsxFileName, lineNumber: 456 } },
                        React.createElement(
                          'th',
                          {
                            className: 'px-4 py-2',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 457 },
                          },
                          'Action'
                        ),
                        React.createElement(
                          'th',
                          {
                            className: 'px-4 py-2',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 458 },
                          },
                          'Date & Time'
                        ),
                        React.createElement(
                          'th',
                          {
                            className: 'px-4 py-2',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 459 },
                          },
                          'Device'
                        )
                      )
                    ),
                    React.createElement(
                      'tbody',
                      {
                        className: 'divide-y divide-stone-50',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 462 },
                      },
                      activities.map((act, idx) =>
                        React.createElement(
                          'tr',
                          {
                            key: idx,
                            className: 'hover:bg-stone-50',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 464 },
                          },
                          React.createElement(
                            'td',
                            {
                              className: 'px-4 py-3 text-xs font-medium text-stone-900',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 465 },
                            },
                            act.action
                          ),
                          React.createElement(
                            'td',
                            {
                              className: 'px-4 py-3 text-xs text-stone-500',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 466 },
                            },
                            act.date
                          ),
                          React.createElement(
                            'td',
                            {
                              className: 'px-4 py-3 text-xs text-stone-500 font-mono',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 467 },
                            },
                            act.device
                          )
                        )
                      )
                    )
                  )
                )
              )
            )
          )
        )
      )
    )
  );
};

export default AdminProfile;
